/**
 * Coolman Workforce · Company Calendar (spec 14).
 *
 * Comprehensive Phase B calendar surface — Month / Week / Agenda views,
 * five-filter bar with session persistence, unified data endpoint,
 * event detail drawer, quick-add via existing EventDialog, calendar
 * legend using the approved Colour Standards.
 *
 * Everything lives in this one file to keep imports lean; sub-render
 * helpers are private components inside the same module.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarBlank, Plus, X, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import EventDialog from "./EventDialog";
import { workforceApi, STATUS_TONE, formatDate, formatDateRange } from "./workforceApi";

const FILTER_STORAGE_KEY = "wf.calendar.filters.v1";
const VIEW_STORAGE_KEY = "wf.calendar.view.v1";

const BIRTHDAY_COLOUR = "#14B8A6";
// Overlays that are not event types but always appear on the calendar
// (spec 12 · Holiday Management + Birthday Recognition directive).
const CALENDAR_OVERLAYS = [
  { label: "Birthday",          colour: BIRTHDAY_COLOUR },
  { label: "Statutory Holiday", colour: "#4169E1" },
  { label: "Company Holiday",   colour: "#228B22" },
];

const VIEWS = [
  { key: "day",    label: "Day" },
  { key: "month",  label: "Month" },
  { key: "week",   label: "Week" },
  { key: "agenda", label: "Agenda" },
];

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
const iso = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => { const [y,m,dd] = s.split("-").map(Number); return new Date(y, m-1, dd); };
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
const sameDay = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const startOfWeek = (d) => addDays(d, -d.getDay());
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth()+1, 0);

// ---------------------------------------------------------------------------
// Owner component
// ---------------------------------------------------------------------------
export default function CalendarTab({ employees, eventTypes, meta, onReload, readOnly }) {
  const [view, setView] = useState(() => sessionStorage.getItem(VIEW_STORAGE_KEY) || "month");
  const [cursor, setCursor] = useState(() => new Date());
  const [filters, setFilters] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || "{}"); }
    catch { return {}; }
  });
  const [data, setData] = useState({ events: [], holidays: [], birthdays: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);        // event opened in detail panel
  const [quickAdd, setQuickAdd] = useState(null);        // { date } for quick-add dialog
  useEffect(() => sessionStorage.setItem(VIEW_STORAGE_KEY, view), [view]);
  useEffect(() => sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters)), [filters]);

  // Compute [start, end] for the current cursor + view
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (view === "month") {
      return [startOfWeek(startOfMonth(cursor)), addDays(startOfWeek(endOfMonth(cursor)), 41)];
    }
    if (view === "week") {
      const s = startOfWeek(cursor);
      return [s, addDays(s, 6)];
    }
    // agenda: 30 days from cursor
    return [cursor, addDays(cursor, 29)];
  }, [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start: iso(rangeStart), end: iso(rangeEnd), ...filters };
      // strip empty
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await workforceApi.calendar(params);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calendar load failed");
    } finally { setLoading(false); }
  }, [rangeStart, rangeEnd, filters]);
  useEffect(() => { load(); }, [load]);

  // Move cursor helpers
  const goToday = () => setCursor(new Date());
  const goPrev = () => setCursor((c) => view === "day" ? addDays(c, -1) : view === "month" ? new Date(c.getFullYear(), c.getMonth()-1, 1) : view === "week" ? addDays(c, -7) : addDays(c, -30));
  const goNext = () => setCursor((c) => view === "day" ? addDays(c, 1) : view === "month" ? new Date(c.getFullYear(), c.getMonth()+1, 1) : view === "week" ? addDays(c, 7) : addDays(c, 30));

  return (
    <div data-testid="workforce-calendar-tab" className="space-y-4">
      {/* Nav + view switch */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button type="button" onClick={goToday} data-testid="wf-cal-today"
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            Today
          </button>
          <button type="button" onClick={goPrev} data-testid="wf-cal-prev"
                  className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:bg-slate-50">
            <ArrowLeft size={14} weight="bold" />
          </button>
          <button type="button" onClick={goNext} data-testid="wf-cal-next"
                  className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:bg-slate-50">
            <ArrowRight size={14} weight="bold" />
          </button>
          <input type="date" value={iso(cursor)} data-testid="wf-cal-jump"
                 onChange={(e) => { const v = e.target.value; if (v) setCursor(parseIso(v)); }}
                 className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs" />
        </div>
        <div className="ml-2 font-display text-lg font-semibold text-slate-900" data-testid="wf-cal-title">
          {formatCursorTitle(view, cursor, rangeStart, rangeEnd)}
        </div>
        <div className="ml-auto inline-flex rounded-md border border-slate-300 bg-white p-0.5">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" data-testid={`wf-cal-view-${v.key}`}
                    onClick={() => setView(v.key)}
                    className={`rounded-sm px-3 py-1 text-xs font-semibold ${view === v.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <CalendarFilters filters={filters} setFilters={setFilters} employees={employees} eventTypes={eventTypes} meta={meta} />

      {/* Main grid + detail panel */}
      <div className={`grid gap-4 ${selected ? "lg:grid-cols-[1fr_320px]" : ""}`}>
        <div>
          {loading ? (
            <div className="h-96 animate-pulse rounded-lg border border-slate-200 bg-slate-50" />
          ) : view === "day" ? (
            <DayView cursor={cursor} data={data} employees={employees} onSelectEvent={setSelected} onQuickAdd={(d) => setQuickAdd({ date: iso(d) })} readOnly={readOnly} />
          ) : view === "month" ? (
            <MonthView rangeStart={rangeStart} cursor={cursor} data={data} onSelectEvent={setSelected} onQuickAdd={(d) => setQuickAdd({ date: iso(d) })} readOnly={readOnly} />
          ) : view === "week" ? (
            <WeekView rangeStart={rangeStart} data={data} onSelectEvent={setSelected} onQuickAdd={(d) => setQuickAdd({ date: iso(d) })} readOnly={readOnly} />
          ) : (
            <AgendaView rangeStart={rangeStart} rangeEnd={rangeEnd} data={data} onSelectEvent={setSelected} />
          )}
          <CalendarLegend eventTypes={eventTypes} />
        </div>
        {selected && (
          <EventDetailPanel event={selected} onClose={() => setSelected(null)} />
        )}
      </div>

      {/* Quick add reuses the existing EventDialog */}
      {quickAdd && (
        <EventDialog
          open
          defaultStartDate={quickAdd.date}
          employees={employees}
          eventTypes={eventTypes.filter((t) => ["vacation","sick","personal","training","company_event"].includes(t.code))}
          onClose={() => setQuickAdd(null)}
          onSaved={() => { setQuickAdd(null); onReload(); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters bar
// ---------------------------------------------------------------------------
function CalendarFilters({ filters, setFilters, employees, eventTypes, meta }) {
  const set = (k, v) => setFilters({ ...filters, [k]: v || undefined });
  const clear = () => setFilters({});
  const hasAny = Object.values(filters).some(Boolean);
  return (
    <div data-testid="wf-cal-filters" className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
      <select data-testid="wf-cal-filter-employee" value={filters.employee_id || ""} onChange={(e) => set("employee_id", e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs">
        <option value="">All employees</option>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
      </select>
      <select data-testid="wf-cal-filter-work-group" value={filters.work_group || ""} onChange={(e) => set("work_group", e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs">
        <option value="">All Work Groups</option>
        {(meta?.work_groups || []).map((g) => <option key={g} value={g}>{g}</option>)}
      </select>
      <select data-testid="wf-cal-filter-event-type" value={filters.event_type || ""} onChange={(e) => set("event_type", e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs">
        <option value="">All event types</option>
        {eventTypes.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
      </select>
      <select data-testid="wf-cal-filter-status" value={filters.status || ""} onChange={(e) => set("status", e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs">
        <option value="">Any status</option>
        {(meta?.event_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {hasAny && (
        <button data-testid="wf-cal-filter-clear" type="button" onClick={clear}
                className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500 hover:text-slate-900">Clear</button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event chip (used inside cells)
// ---------------------------------------------------------------------------
function EventChip({ event, onClick, compact }) {
  return (
    <button type="button" onClick={onClick} data-testid={`wf-cal-event-${event.id}`}
            className={`w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium text-white hover:opacity-90 ${compact ? "" : "mb-0.5"}`}
            style={{ backgroundColor: event.event_type_colour }}>
      <span className="truncate">{event.employee_name} · {event.event_type_name}</span>
    </button>
  );
}

function HolidayChip({ holiday, onClick }) {
  const colour = holiday.colour || (holiday.category === "statutory" ? "#4169E1" : "#228B22");
  return (
    <button type="button" onClick={onClick} data-testid={`wf-cal-holiday-${holiday.id}`}
            className="mb-0.5 w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: colour }}>
      {holiday.name}
    </button>
  );
}

function BirthdayChip({ birthday }) {
  return (
    <div data-testid={`wf-cal-birthday-${birthday.employee_id}-${birthday.date}`}
         className="mb-0.5 truncate rounded px-1.5 py-0.5 text-[11px] font-medium text-white"
         style={{ backgroundColor: BIRTHDAY_COLOUR }}>
      🎂 {birthday.full_name}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month view
// ---------------------------------------------------------------------------
function MonthView({ rangeStart, cursor, data, onSelectEvent, onQuickAdd, readOnly }) {
  const today = new Date();
  const days = [];
  for (let i = 0; i < 42; i++) days.push(addDays(rangeStart, i));
  return (
    <div data-testid="wf-cal-month-view" className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="border-r border-slate-200 px-2 py-1 last:border-r-0">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const dISO = iso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const evs = data.events.filter((e) => dISO >= e.start_date && dISO <= e.end_date);
          const hols = data.holidays.filter((h) => h.date === dISO);
          const bdays = data.birthdays.filter((b) => b.date === dISO);
          return (
            <div key={dISO} data-testid={`wf-cal-day-${dISO}`}
                 className={`group relative min-h-[110px] border-b border-r border-slate-100 p-1 last:border-r-0 ${inMonth ? "bg-white" : "bg-slate-50/50 text-slate-400"} ${isToday ? "bg-amber-50" : ""}`}>
              <div className="flex items-center justify-between text-[11px]">
                <span className={`font-semibold ${isToday ? "rounded-full bg-slate-900 px-1.5 text-white" : "text-slate-700"}`}>{d.getDate()}</span>
                {!readOnly && (
                  <button type="button" onClick={() => onQuickAdd(d)} data-testid={`wf-cal-quickadd-${dISO}`}
                          className="rounded p-0.5 text-slate-300 opacity-0 hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100">
                    <Plus size={12} weight="bold" />
                  </button>
                )}
              </div>
              <div className="mt-1 space-y-0.5">
                {hols.map((h) => <HolidayChip key={h.id} holiday={h} />)}
                {bdays.map((b) => <BirthdayChip key={`${b.employee_id}-${b.date}`} birthday={b} />)}
                {evs.slice(0, 3).map((e) => <EventChip key={e.id} event={e} onClick={() => onSelectEvent(e)} />)}
                {evs.length > 3 && <div className="text-[10px] font-semibold text-slate-500">+{evs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Week view
// ---------------------------------------------------------------------------
function WeekView({ rangeStart, data, onSelectEvent, onQuickAdd, readOnly }) {
  const today = new Date();
  const days = Array.from({ length: 7 }, (_, i) => addDays(rangeStart, i));
  return (
    <div data-testid="wf-cal-week-view" className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {days.map((d) => (
          <div key={iso(d)} className="border-r border-slate-200 px-2 py-1.5 text-[11px] font-semibold last:border-r-0">
            <div className="text-slate-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
            <div className={`text-sm ${sameDay(d, today) ? "rounded bg-amber-100 px-1.5 text-slate-900" : "text-slate-700"}`}>{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const dISO = iso(d);
          const evs = data.events.filter((e) => dISO >= e.start_date && dISO <= e.end_date);
          const hols = data.holidays.filter((h) => h.date === dISO);
          const bdays = data.birthdays.filter((b) => b.date === dISO);
          return (
            <div key={dISO} data-testid={`wf-cal-week-day-${dISO}`} className={`group relative min-h-[280px] border-b border-r border-slate-100 p-2 last:border-r-0 ${sameDay(d, today) ? "bg-amber-50" : ""}`}>
              {!readOnly && (
                <button type="button" onClick={() => onQuickAdd(d)}
                        className="absolute right-1 top-1 rounded p-0.5 text-slate-300 opacity-0 hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100">
                  <Plus size={12} weight="bold" />
                </button>
              )}
              <div className="space-y-1">
                {hols.map((h) => <HolidayChip key={h.id} holiday={h} />)}
                {bdays.map((b) => <BirthdayChip key={`${b.employee_id}-${b.date}`} birthday={b} />)}
                {evs.map((e) => <EventChip key={e.id} event={e} onClick={() => onSelectEvent(e)} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agenda view — chronological list grouped by date
// ---------------------------------------------------------------------------
function AgendaView({ rangeStart, rangeEnd, data, onSelectEvent }) {
  const today = new Date();
  const rows = [];
  const days = Math.round((rangeEnd - rangeStart) / (86400 * 1000)) + 1;
  for (let i = 0; i < days; i++) {
    const d = addDays(rangeStart, i);
    const dISO = iso(d);
    const evs = data.events.filter((e) => dISO >= e.start_date && dISO <= e.end_date);
    const hols = data.holidays.filter((h) => h.date === dISO);
    const bdays = data.birthdays.filter((b) => b.date === dISO);
    if (evs.length + hols.length + bdays.length === 0) continue;
    rows.push({ d, dISO, evs, hols, bdays, isToday: sameDay(d, today) });
  }
  return (
    <div data-testid="wf-cal-agenda-view" className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      {rows.length === 0 && (
        <div className="p-6 text-center text-sm text-slate-500">No workforce activity in this range.</div>
      )}
      {rows.map((row) => (
        <div key={row.dISO} data-testid={`wf-cal-agenda-day-${row.dISO}`} className={`border-b border-slate-100 p-3 ${row.isToday ? "bg-amber-50" : ""}`}>
          <div className="mb-1.5 flex items-baseline gap-2">
            <div className={`text-sm font-semibold ${row.isToday ? "text-slate-900" : "text-slate-700"}`}>{row.d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
            {row.isToday && <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white">Today</span>}
          </div>
          <div className="space-y-1">
            {row.hols.map((h) => (
              <div key={h.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-sm">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: h.colour || (h.category === "statutory" ? "#4169E1" : "#228B22") }} />
                <span className="font-medium text-slate-900">{h.name}</span>
                <span className="text-[11px] text-slate-500">{h.category === "statutory" ? "Statutory" : "Company"} Holiday</span>
              </div>
            ))}
            {row.bdays.map((b) => (
              <div key={`${b.employee_id}-${b.date}`} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-sm">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BIRTHDAY_COLOUR }} />
                <span className="font-medium text-slate-900">🎂 {b.full_name}</span>
                <span className="text-[11px] text-slate-500">Birthday</span>
              </div>
            ))}
            {row.evs.map((e) => (
              <button key={e.id} type="button" onClick={() => onSelectEvent(e)} data-testid={`wf-cal-agenda-event-${e.id}`}
                      className="flex w-full items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-left text-sm hover:bg-slate-100">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: e.event_type_colour }} />
                <span className="font-medium text-slate-900">{e.employee_name}</span>
                <span className="text-[11px] text-slate-500">{e.event_type_name}</span>
                <span className="ml-auto text-[11px] text-slate-600">{formatDateRange(e.start_date, e.end_date, e.start_half, e.end_half)}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[e.status]}`}>{e.status}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event Detail panel (spec 14 § 4 — read-only)
// ---------------------------------------------------------------------------
function EventDetailPanel({ event, onClose }) {
  const goToEmployee = () => {
    window.dispatchEvent(new CustomEvent("workforce:navigate-tab", { detail: "employees" }));
    onClose();
  };
  return (
    <aside data-testid="wf-cal-detail-panel" className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Event</div>
          <button
            type="button"
            onClick={goToEmployee}
            data-testid="wf-cal-detail-view-employee"
            className="mt-0.5 text-left text-base font-semibold text-slate-900 underline decoration-slate-300 decoration-1 underline-offset-2 hover:decoration-slate-800"
            title="View employee record"
          >
            {event.employee_name} →
          </button>
        </div>
        <button type="button" onClick={onClose} data-testid="wf-cal-detail-close" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
          <X size={14} weight="bold" />
        </button>
      </div>
      <dl className="space-y-2 text-sm">
        <Row label="Event Type">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: event.event_type_colour }} />
            {event.event_type_name}
          </span>
        </Row>
        <Row label="Status">
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[event.status]}`}>{event.status}</span>
        </Row>
        <Row label="Dates">{formatDateRange(event.start_date, event.end_date, event.start_half, event.end_half)}</Row>
        {(event.start_half || event.end_half) && (
          <Row label="Half-Day">
            {[event.start_half && "Start ½ day", event.end_half && "End ½ day"].filter(Boolean).join(" · ")}
          </Row>
        )}
        <Row label="Duration">{event.duration_days} day{event.duration_days === 1 ? "" : "s"}</Row>
        {event.notes && <Row label="Notes"><span className="whitespace-pre-wrap text-slate-700">{event.notes}</span></Row>}
        <Row label="Attachments">
          <span className="text-slate-400">— none —</span>
        </Row>
        <Row label="Approval History">
          <span className="text-slate-400 text-[11px]">
            <ClipboardText size={12} weight="bold" className="mr-1 inline" />
            Full history available once Phase C ships. Current status: {event.status}
            {event.approver ? ` by ${event.approver}` : ""}
            {event.approved_at ? ` on ${formatDate(event.approved_at.slice(0,10))}` : ""}.
          </span>
        </Row>
      </dl>
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
        Editing, approve, and decline actions live in the <strong>Events</strong> tab. The Company Calendar is a visualization + planning surface.
      </div>
    </aside>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-24 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-slate-800">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legend — data-driven (PO directive 2026-07-03 · Company Calendar Legend).
// Automatically reflects every enabled event type plus the three calendar
// overlays (Birthday · Statutory Holiday · Company Holiday). Adding a new
// event type in Settings surfaces it here without code changes.
// ---------------------------------------------------------------------------
function CalendarLegend({ eventTypes }) {
  const items = [
    ...(eventTypes || []).filter((t) => t.enabled !== false).map((t) => ({ label: t.name, colour: t.colour })),
    ...CALENDAR_OVERLAYS,
  ];
  return (
    <div data-testid="wf-cal-legend" className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px]">
      <span className="mr-1 flex items-center gap-1 font-semibold text-slate-600">
        <CalendarBlank size={12} weight="bold" /> Legend
      </span>
      {items.map((l) => (
        <span key={l.label} data-testid={`wf-cal-legend-item-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="inline-flex items-center gap-1.5 text-slate-700">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: l.colour }} />
          {l.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Title formatter
// ---------------------------------------------------------------------------
function formatCursorTitle(view, cursor, rs, re) {
  if (view === "day") return cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  if (view === "month") return cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  if (view === "week")  return `${rs.toLocaleDateString(undefined, { month:"short", day:"numeric" })} – ${re.toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}`;
  return `${rs.toLocaleDateString(undefined, { month:"short", day:"numeric" })} – ${re.toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}`;
}

// ---------------------------------------------------------------------------
// Day view — single-day operational snapshot (PO directive 2026-07-03).
// Sections: Employees Working · Away · On Call · Company Holidays ·
// Statutory Holidays · Birthdays. Answers "who's where today?" in seconds.
// ---------------------------------------------------------------------------
function DayView({ cursor, data, employees, onSelectEvent, onQuickAdd, readOnly }) {
  const today = new Date();
  const dISO = iso(cursor);
  const isToday = sameDay(cursor, today);

  const dayEvents = data.events.filter((e) => dISO >= e.start_date && dISO <= e.end_date);
  const onCall = dayEvents.filter((e) => e.event_type_code === "on_call");
  const away = dayEvents.filter((e) => e.event_type_code !== "on_call");
  const awayEmployeeIds = new Set([...away, ...onCall].map((e) => e.employee_id));
  const activeEmployees = (employees || []).filter((emp) => emp.status === "Active");
  const working = activeEmployees.filter((emp) => !awayEmployeeIds.has(emp.id));

  const dayHolidays = data.holidays.filter((h) => h.date === dISO);
  const statutory = dayHolidays.filter((h) => (h.category || (h.is_statutory ? "statutory" : "company")) === "statutory");
  const company = dayHolidays.filter((h) => (h.category || (h.is_statutory ? "statutory" : "company")) === "company");
  const dayBirthdays = data.birthdays.filter((b) => b.date === dISO);

  return (
    <div data-testid="wf-cal-day-view" className="space-y-3">
      <div className={`flex items-center justify-between rounded-lg border p-3 ${isToday ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Daily Snapshot</div>
          <div className="text-lg font-semibold text-slate-900">{cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
        </div>
        {!readOnly && (
          <button type="button" onClick={() => onQuickAdd(cursor)} data-testid={`wf-cal-day-quickadd-${dISO}`}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-xs font-semibold text-ink-inverse hover:bg-slate-900">
            <Plus size={12} weight="bold" /> Quick Add
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <DaySection testid="wf-cal-day-working" title="Employees Working" count={working.length}>
          {working.length === 0
            ? <DayEmpty text="Nobody scheduled to work." />
            : working.map((emp) => (
                <div key={emp.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-1.5 text-sm">
                  <span className="font-medium text-slate-900">{emp.full_name}</span>
                  <span className="text-[11px] text-slate-500">{emp.work_group || "—"}</span>
                </div>
              ))}
        </DaySection>
        <DaySection testid="wf-cal-day-away" title="Employees Away" count={away.length}>
          {away.length === 0 ? <DayEmpty text="Everyone's in." /> : away.map((e) => <DayEventRow key={e.id} event={e} onClick={() => onSelectEvent(e)} />)}
        </DaySection>
        <DaySection testid="wf-cal-day-oncall" title="On Call" count={onCall.length}>
          {onCall.length === 0 ? <DayEmpty text="No one on call." /> : onCall.map((e) => <DayEventRow key={e.id} event={e} onClick={() => onSelectEvent(e)} />)}
        </DaySection>
        <DaySection testid="wf-cal-day-statutory" title="Statutory Holidays" count={statutory.length}>
          {statutory.length === 0
            ? <DayEmpty text="No statutory holiday." />
            : statutory.map((h) => (
                <div key={h.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: h.colour || "#4169E1" }} />
                  <span className="font-medium text-slate-900">{h.name}</span>
                </div>
              ))}
        </DaySection>
        <DaySection testid="wf-cal-day-company-holidays" title="Company Holidays" count={company.length}>
          {company.length === 0
            ? <DayEmpty text="No company holiday." />
            : company.map((h) => (
                <div key={h.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: h.colour || "#228B22" }} />
                  <span className="font-medium text-slate-900">{h.name}</span>
                </div>
              ))}
        </DaySection>
        <DaySection testid="wf-cal-day-birthdays" title="Birthdays" count={dayBirthdays.length}>
          {dayBirthdays.length === 0
            ? <DayEmpty text="No birthdays today." />
            : dayBirthdays.map((b) => (
                <div key={b.employee_id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BIRTHDAY_COLOUR }} />
                  <span className="font-medium text-slate-900">🎂 {b.full_name}</span>
                </div>
              ))}
        </DaySection>
      </div>
    </div>
  );
}

function DaySection({ testid, title, count, children }) {
  return (
    <section data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{count}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function DayEventRow({ event, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-left text-sm hover:bg-slate-100">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: event.event_type_colour }} />
      <span className="font-medium text-slate-900">{event.employee_name}</span>
      <span className="text-[11px] text-slate-500">{event.event_type_name}</span>
    </button>
  );
}

function DayEmpty({ text }) {
  return <div className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">{text}</div>;
}
